import { useState } from 'react';
import { Select, Input, Space, DatePicker, Upload, Tag } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../../../../contexts/AuthContext';
import { useCountryArray } from '../../../../utils/countryArray';
import { compressFile } from '../../../../utils/filesManagement/compressFile';
import { useCountryCodeInfos } from '../../../owners/form/utils/useCountryCodeInfos';
import {
  getPostalCodeLength,
  requiresDepartment
} from '../../../owners/form/utils/addressCountryRules';
import { createPhoneNumberFieldConfig } from '../../../../utils/formFields/phoneNumberField';

const { Option } = Select;
const { Dragger } = Upload;
/**
 * Custom hook for generating main fields used in profile creation/update form.
 * @param {Object} params - Parameters for the hook.
 * @param {function} params.setDocumentsFileList - Function to set the documents file list.
 * @param {Object} params.documentsFileList - Current documents file list.
 * @returns {Object} - Object containing mainFields array.
 */
export const useFields = ({
  enums,
  setDocumentsFileList,
  documentsFileList,
  setRemovedFiles,
  form,
  country,
  setCountry
}) => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const countries = useCountryArray();
  const { handleCountryCodeInfos } = useCountryCodeInfos(country);

  const [cityOptions, setCityOptions] = useState([]);

  const filteredEnums = enums?.civility?.filter(
    (civility) => !civility.includes('&')
  );

  /**
   * Function to filter options based on input.
   * @param {string} input - The input string to filter options.
   * @param {Object} option - The option object to filter.
   * @returns {boolean} - Whether the option should be displayed.
   */
  const filterOption = (input, option) =>
    option.props.value.toLowerCase().indexOf(input.toLowerCase()) >= 0;

  /**
   * Function to set documents dragger properties.
   * @param {string} name - The name of the document.
   * @returns {Object} - Object containing properties for the documents Dragger component.
   */
  const documentsDraggerProps = (name) => ({
    onRemove: (file) => {
      const fileId = file.id || file.uid;
      if (fileId) {
        setRemovedFiles((prevRemovedFiles) => [...prevRemovedFiles, fileId]);
      }

      setDocumentsFileList((prevFileList) => {
        const updatedFileList = { ...prevFileList };
        delete updatedFileList[name];
        return updatedFileList;
      });
    },
    beforeUpload: async (file) => {
      if (name === 'identification_photo') {
        const newFile = await compressFile(file);
        setDocumentsFileList((prevFileList) => ({
          ...prevFileList,
          [name]: [newFile]
        }));
      } else {
        setDocumentsFileList((prevFileList) => ({
          ...prevFileList,
          [name]: [file]
        }));
      }
      return false;
    },
    fileList: documentsFileList[name] || []
  });

  /**
   * Main fields array for the profile form.
   */
  const mainFields = [
    {
      name: ['civility'],
      rules: [{ required: true }],
      input: (
        <Select>
          {(
            (user?.role !== 'users:OWNER' ? filteredEnums : enums.civility) ||
            []
          ).map((civility) => (
            <Option key={civility} value={civility}>
              <Tag>{t(`pensioners.tags.${civility}`)}</Tag>
            </Option>
          ))}
        </Select>
      )
    },
    {
      name: ['last_name'],
      rules: [{ required: true }]
    },
    {
      name: ['first_name'],
      rules: [{ required: true }]
    },
    ...(user?.role === 'users:PENSIONER'
      ? [
          {
            name: ['birth_date'],
            rules: [{ required: true }],
            input: (
              <DatePicker
                format="DD/MM/YYYY"
                panelRender={() => null}
                preserveInvalidOnBlur
                placeholder="JJ/MM/AAAA"
              />
            )
          }
        ]
      : []),
    createPhoneNumberFieldConfig('cell_phone_number'),
    ...(user?.role === 'users:OWNER'
      ? [
          createPhoneNumberFieldConfig('phone_number'),
          {
            name: ['email'],
            rules: [{ required: true }]
          }
        ]
      : []),
    ...(user?.role === 'users:OWNER'
      ? [
          {
            name: ['first_profession']
          },
          {
            name: ['second_profession']
          },
          {
            name: ['address', 'number']
          },
          {
            name: ['address', 'street']
          },
          {
            label: 'address.country',
            name: ['address', 'country'],
            input: (
              <Select
                showSearch
                filterOption={filterOption}
                onChange={(value) => {
                  setCountry(value);
                  form.setFieldValue(['address', 'postal_code'], undefined);
                  form.setFieldValue(['address', 'city'], undefined);
                  form.setFieldValue(['address', 'state'], undefined);
                  setCityOptions([]);
                }}
              >
                {(countries || []).map((value) => (
                  <Option key={value} value={value}>
                    <Tag>{value}</Tag>
                  </Option>
                ))}
              </Select>
            ),
            rules: [{ required: true }]
          },
          {
            label: 'address.postal_code',
            name: ['address', 'postal_code'],
            input: (
              <Input
                onChange={(e) => {
                  const postalCode = e.target.value;

                  form.setFieldValue(['address', 'city'], undefined);
                  form.setFieldValue(['address', 'state'], undefined);
                  setCityOptions([]);

                  if (postalCode.length < getPostalCodeLength(country)) return;

                  handleCountryCodeInfos(postalCode).then(
                    ({ cities, city, state }) => {
                      form.setFieldValue(
                        ['address', 'state'],
                        state || undefined
                      );

                      if (cities.length > 1) {
                        setCityOptions(cities);
                      } else {
                        form.setFieldValue(
                          ['address', 'city'],
                          city || undefined
                        );
                        setCityOptions([]);
                      }
                    }
                  );
                }}
              />
            ),
            dependencies: ['address', 'country'],
            rules: [{ required: true }]
          },
          {
            label: requiresDepartment(country)
              ? 'address.state'
              : 'address.province',
            name: ['address', 'state'],
            dependencies: [['address', 'country']],
            rules: [{ required: requiresDepartment(country) }]
          },
          {
            label: 'address.city',
            name: ['address', 'city'],
            rules: [{ required: true }],
            input:
              cityOptions.length > 1 ? (
                <Select
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {cityOptions.map((city) => (
                    <Option key={city} value={city}>
                      {city}
                    </Option>
                  ))}
                </Select>
              ) : (
                <Input />
              )
          }
        ]
      : [
          {
            name: ['profession']
          },
          {
            name: ['principal_language']
          },
          {
            label: 'criminal_record_extract',
            input: (
              <Dragger {...documentsDraggerProps('criminal_record_extract')}>
                <Space>
                  <UploadOutlined />
                  {t('users.form.upload_file')}
                </Space>
              </Dragger>
            )
          },
          {
            label: 'identification_photocopy',
            input: (
              <Dragger {...documentsDraggerProps('identification_photocopy')}>
                <Space>
                  <UploadOutlined />
                  {t('users.form.upload_file')}
                </Space>
              </Dragger>
            )
          },
          {
            label: 'identification_photo',
            input: (
              <Dragger {...documentsDraggerProps('identification_photo')}>
                <Space>
                  <UploadOutlined />
                  {t('users.form.upload_file')}
                </Space>
              </Dragger>
            )
          }
        ])
  ];

  return {
    mainFields
  };
};
